import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, RefreshCw } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const AlternativesPage = () => {
  const popularAlternatives = [
    {
      original: 'Salesforce',
      alternatives: [
        { name: 'HubSpot CRM', rating: 4.8, pricing: 'Free - $1,200/mo' },
        { name: 'Pipedrive', rating: 4.5, pricing: '$15 - $99/user/mo' },
        { name: 'Zoho CRM', rating: 4.4, pricing: 'Free - $52/user/mo' }
      ]
    },
    {
      original: 'Slack',
      alternatives: [
        { name: 'Microsoft Teams', rating: 4.6, pricing: 'Free - $12.50/user/mo' },
        { name: 'Discord', rating: 4.7, pricing: 'Free - $9.99/mo' },
        { name: 'Google Chat', rating: 4.3, pricing: 'Free - $12/user/mo' }
      ]
    },
    {
      original: 'Adobe Creative Cloud',
      alternatives: [
        { name: 'Canva', rating: 4.8, pricing: 'Free - $12.99/mo' },
        { name: 'Figma', rating: 4.9, pricing: 'Free - $15/editor/mo' },
        { name: 'Affinity Suite', rating: 4.6, pricing: '$69.99 one-time' }
      ]
    },
    {
      original: 'Mailchimp',
      alternatives: [
        { name: 'ConvertKit', rating: 4.7, pricing: 'Free - $59/mo' },
        { name: 'Brevo', rating: 4.5, pricing: 'Free - $65/mo' },
        { name: 'ActiveCampaign', rating: 4.6, pricing: '$29 - $259/mo' }
      ]
    },
    {
      original: 'Asana',
      alternatives: [
        { name: 'Monday.com', rating: 4.7, pricing: 'Free - $24/seat/mo' },
        { name: 'ClickUp', rating: 4.6, pricing: 'Free - $19/member/mo' },
        { name: 'Notion', rating: 4.9, pricing: 'Free - $15/user/mo' }
      ]
    },
    {
      original: 'Zoom',
      alternatives: [
        { name: 'Google Meet', rating: 4.5, pricing: 'Free - $12/user/mo' },
        { name: 'Microsoft Teams', rating: 4.6, pricing: 'Free - $12.50/user/mo' },
        { name: 'Whereby', rating: 4.4, pricing: 'Free - $14.99/mo' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <RefreshCw className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Tool Alternatives</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Looking for alternatives to popular software? Find comparable tools that might better fit your needs and budget.
            </p>
          </div>

          {/* Alternatives Grid */}
          <div className="space-y-8">
            {popularAlternatives.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Alternatives to <span className="text-emerald-600">{item.original}</span>
                  </h2>
                  <Button variant="link" className="text-emerald-600">
                    View All <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {item.alternatives.map((alt, altIndex) => (
                    <div 
                      key={altIndex} 
                      className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 mb-2">{alt.name}</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium">{alt.rating}</span>
                      </div>
                      <p className="text-sm text-emerald-600">{alt.pricing}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-12 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Can't Find What You're Looking For?</h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
              Use our comparison tool to find alternatives for any software.
            </p>
            <Button asChild size="lg" className="bg-white text-emerald-600 hover:bg-gray-100">
              <Link to="/compare">Compare Tools</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AlternativesPage;